import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import { FileText, MessageSquare, Plus, Check, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMatchingListings } from "@/hooks/useMatchingListings";
import { useMatchingEngineListings } from "@/hooks/useMatchingEngineListings";
import { useMatchingPartsListings } from "@/hooks/useMatchingPartsListings";
import { useMatchingJobListings } from "@/hooks/useMatchingJobListings";
import { useMatchingServiceListings } from "@/hooks/useMatchingServiceListings";
import OptimizedImage from "@/components/OptimizedImage";
import heroPlaceholder from '@/assets/hero-helicopter-new.jpg';
import { addCacheBuster } from '@/utils/iconUtils';

type AssetType = 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';

interface UnifiedBuyRequestResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: any;
  assetType: AssetType;
  onContactFlow: () => void;
}

const ASSET_LABELS: Record<AssetType, { singular: string; buttonText: string; createPath: string }> = {
  helicopter: { singular: 'Helicopter', buttonText: 'Offer Your Helicopter', createPath: '/create-listing' },
  engine: { singular: 'Engine', buttonText: 'Offer Your Engine', createPath: '/create-engine' },
  parts: { singular: 'Part', buttonText: 'Offer Your Part', createPath: '/create-parts' },
  jobs: { singular: 'Position', buttonText: 'Offer Position', createPath: '/create-job' },
  services: { singular: 'Service', buttonText: 'Offer Service', createPath: '/create-service' },
};

const UnifiedBuyRequestResponseModal = ({
  isOpen,
  onClose,
  request,
  assetType,
  onContactFlow,
}: UnifiedBuyRequestResponseModalProps) => {
  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
    };
    getUser();
  }, []);

  // Use appropriate hook based on asset type
  const helicopterListings = useMatchingListings(
    assetType === 'helicopter' ? request?.helicopter_id : null,
    request?.listing_type || '',
    assetType === 'helicopter' ? userId : null
  );

  const engineListings = useMatchingEngineListings(
    assetType === 'engine' ? request?.manufacturer : null,
    assetType === 'engine' ? request?.model : null,
    request?.listing_type || '',
    assetType === 'engine' ? userId : null
  );

  const partsListings = useMatchingPartsListings(
    assetType === 'parts' ? request?.category : null,
    request?.listing_type || '',
    assetType === 'parts' ? userId : null
  );

  const jobListings = useMatchingJobListings(
    assetType === 'jobs' ? request?.job_category : null,
    request?.listing_type || '',
    assetType === 'jobs' ? userId : null
  );

  const serviceListings = useMatchingServiceListings(
    assetType === 'services' ? request?.service_category : null,
    request?.listing_type || '',
    assetType === 'services' ? userId : null
  );

  // Get the appropriate listings based on asset type
  const getListingsForAssetType = () => {
    switch (assetType) {
      case 'helicopter':
        return { matchingListings: helicopterListings.matchingListings, loading: helicopterListings.loading };
      case 'engine':
        return { matchingListings: engineListings.matchingListings, loading: engineListings.loading };
      case 'parts':
        return { matchingListings: partsListings.matchingListings, loading: partsListings.loading };
      case 'jobs':
        return { matchingListings: jobListings.matchingListings, loading: jobListings.loading };
      case 'services':
        return { matchingListings: serviceListings.matchingListings, loading: serviceListings.loading };
      default:
        return { matchingListings: [], loading: false };
    }
  };

  const { matchingListings, loading } = getListingsForAssetType();
  const assetConfig = ASSET_LABELS[assetType];

  const handleLinkListing = async (listingId: string) => {
    if (!userId) return;

    try {
      setSubmitting(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('buy_request_responses')
        .insert({
          request_id: request.id,
          seller_id: user.id,
          linked_listing_id: listingId,
          response_type: 'listing_link',
          message: `I have a matching ${assetConfig.singular.toLowerCase()} that fits your requirements.`,
          seller_name: user.email || 'Seller',
          seller_email: user.email || '',
        });

      if (error) throw error;

      toast({
        title: "Response submitted!",
        description: `Your ${assetConfig.singular.toLowerCase()} has been submitted as a response.`,
      });

      onClose();
    } catch (error) {
      console.error('Error submitting listing response:', error);
      toast({
        title: "Error submitting response",
        description: "Failed to submit your response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateNewListing = () => {
    navigate(`${assetConfig.createPath}?respondingTo=${request.id}`, {
      state: {
        respondingToRequest: request,
      },
    });
    onClose();
  };

  const formatPrice = (listing: any) => {
    // Handle different price fields for different asset types
    const price = listing.price_usd || listing.price || listing.price_from || listing.salary_min;
    if (!price) return 'Price negotiable';
    return `$${price.toLocaleString()}`;
  };

  const getListingImage = (listing: any) => {
    // Handle different image field patterns
    if (listing.images?.length > 0) return listing.images[0];
    if (listing.photos?.length > 0) return listing.photos[0];
    if (listing.heroImage) return listing.heroImage;
    if (listing.helicopters?.icon_url) return listing.helicopters.icon_url;
    return null;
  };

  const getListingSubtitle = (listing: any) => {
    switch (assetType) {
      case 'helicopter':
        return listing.helicopters ? `${listing.helicopters.manufacturer} ${listing.helicopters.model}` : '';
      case 'engine':
        return `${listing.manufacturer} ${listing.model}${listing.variant ? ` ${listing.variant}` : ''}`;
      case 'parts':
        return listing.part_number ? `P/N: ${listing.part_number}` : listing.category || '';
      case 'jobs':
        return listing.company_name || listing.employment_type || '';
      case 'services':
        return listing.company_name || listing.service_category || '';
      default:
        return '';
    }
  };

  const getListingLocation = (listing: any) => {
    if (listing.location) return listing.location;
    const parts = [listing.city, listing.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Respond to Request</DialogTitle>
          <DialogDescription>
            Request: {request.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Response Type Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
              onClick={() => {
                onClose();
                onContactFlow();
              }}
            >
              <CardHeader className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-primary mb-2" />
                <CardTitle>Quick Message</CardTitle>
                <CardDescription>
                  Send a message without linking a specific listing
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-primary/50">
              <CardHeader className="text-center">
                <FileText className="w-12 h-12 mx-auto text-primary mb-2" />
                <CardTitle>{assetConfig.buttonText}</CardTitle>
                <CardDescription>
                  Link an existing listing or create a new one
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          <Separator />

          {/* Matching Listings Section */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Your Matching Listings</h3>
            
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading your listings...
              </div>
            ) : matchingListings.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    You don't have any matching listings yet
                  </p>
                  <Button onClick={handleCreateNewListing}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Matching Listing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {matchingListings.map((listing: any) => (
                  <Card
                    key={listing.id}
                    className={`cursor-pointer transition-all ${
                      selectedListingId === listing.id
                        ? 'border-2 border-primary shadow-lg'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => setSelectedListingId(listing.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                          {getListingImage(listing) ? (
                            <OptimizedImage
                              src={addCacheBuster(getListingImage(listing))}
                              alt={listing.title}
                              className="w-full h-full object-cover"
                              fallbackSrc={heroPlaceholder}
                            />
                          ) : (
                            <OptimizedImage
                              src={heroPlaceholder}
                              alt={listing.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold truncate">{listing.title}</h4>
                            {selectedListingId === listing.id && (
                              <Badge className="ml-2">
                                <Check className="w-3 h-3 mr-1" />
                                Selected
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Price: </span>
                              <span className="font-medium">{formatPrice(listing)}</span>
                            </div>
                            {getListingSubtitle(listing) && (
                              <div className="truncate">
                                <span className="text-muted-foreground">{getListingSubtitle(listing)}</span>
                              </div>
                            )}
                          </div>

                          {getListingLocation(listing) && (
                            <p className="text-sm text-muted-foreground mt-1 truncate">
                              <MapPin className="w-3.5 h-3.5 inline mr-1" />{getListingLocation(listing)}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleCreateNewListing}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Listing Instead
                </Button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {selectedListingId && (
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={() => handleLinkListing(selectedListingId)}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : `Submit Selected ${assetConfig.singular}`}
              </Button>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedBuyRequestResponseModal;
